// Copyright © 2021 Move Closer
import { TokenType } from '@d24/demarked';
/**
 * Converts the passed-in LITs (`ListItemToken[]`) into LIs (`ListItem[]`).
 *
 * @param items - `ListItemToken[]` array to convert.
 *
 * @recursive
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export const convertLITsToLIs = (items) => {
    return items.map(item => {
        // Resolve the ParagraphToken which contains the item's text content.
        const paragraphToken = item.content.find(token => token.type === TokenType.Paragraph);
        if (typeof paragraphToken === 'undefined') {
            throw new Error(`convertLITsToSLIs(): Failed to resolve the [paragraphToken]!\nBroken ListItemToken: ${JSON.stringify(item)}`);
        }
        // Prepare the base object which could be filled-in with more properties down the line.
        const simpleItem = { text: paragraphToken.content };
        // Resolve the ListToken which contains the item's children tokens
        // (in case we deal with the nested lists).
        const listToken = item.content.find(token => token.type === TokenType.List);
        // If the ListToken has been found, use it to populate the `simpleItem` with more properties.
        if (typeof listToken !== 'undefined') {
            simpleItem.children = convertLITsToLIs(listToken.content);
            simpleItem.meta = listToken.meta;
        }
        // Return the constructed object.
        return simpleItem;
    });
};
/**
 * Converts the passed-in LIs (`ListItems`) into LITs (`ListItemTokens`).
 *
 * @param items - `ListItem[]` array to convert.
 *
 * @recursive
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export const convertSLIsToLITs = (items) => {
    return items.map(item => {
        // Construct the base ListItemToken.
        const token = {
            content: [],
            type: TokenType.ListItem
        };
        // Construct the ParagraphToken.
        const paragraphToken = {
            content: item.text,
            raw: item.text,
            type: TokenType.Paragraph
        };
        // Insert the ParagraphToken into the ListItemToken.
        token.content.push(paragraphToken);
        // If the item has any `children`, create the ListToken for this purpose.
        if (typeof item.children !== 'undefined') {
            const listToken = {
                content: convertSLIsToLITs(item.children),
                type: TokenType.List
            };
            if (typeof item.meta !== 'undefined') {
                listToken.meta = item.meta;
            }
            // Insert the ListToken into the ListItemToken.
            token.content.push(listToken);
        }
        // Return the constructed object.
        return token;
    });
};
/**
 * Resolves the raw string representation of the passed-in `ListToken`.
 *
 * @param items - List items to stringify.
 * @param isOrdered - Whether the list is ordered (numbered) or bulleted.
 * @param indentation - Current indentation.
 *
 * @recursive
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export const getListTokenRaw = (items, isOrdered, indentation = '') => {
    return items.reduce((acc, curr, index) => {
        // Resolve the ParagraphToken which contains the item's text content.
        const paragraphToken = curr.content.find(({ type }) => type === TokenType.Paragraph);
        if (typeof paragraphToken === 'undefined') {
            throw new Error(`getListTokenRaw(): Failed to resolve the [paragraphToken]!\nBroken ListItemToken: ${JSON.stringify(curr)}`);
        }
        // Insert the main item's text content.
        acc += `${index > 0 ? '\n' : ''}${indentation}${isOrdered ? (index + 1).toString() + '.' : '-'} ${paragraphToken.content}`;
        // Resolve the ListToken which contains the item's children tokens
        // (in case we deal with the nested lists).
        const listToken = curr.content.find(({ type }) => type === TokenType.List);
        // If the ListToken has been found, use it to further extend the accumulator.
        if (typeof listToken !== 'undefined') {
            acc += '\n' + getListTokenRaw(listToken.content, isOrdered, `${indentation}    `);
        }
        // Return the concatenated accumulator.
        return acc;
    }, '');
};
